/**
 * @file src/orbitersApp.js
 * @description The Orbiters runtime, built by `createOrbitersApp()` (called from Main.js, the
 * composition root). Constructs the scene/world/audio/parameter singletons, wires session
 * bootstrap + lifecycle handlers, and returns `{ start, parameterManager }`. Scene setup, parameter
 * management, interactions, and rendering are all assembled here.
 * @version 2.0.0
 * @author 𝐵𝓇𝓊𝓃𝒶 𝒢𝓊𝒶𝓇𝓃𝒾𝑒𝓇𝒾 
 * @license MIT
 * @date 2024-12-07
 * @memberof CoreModule 
 */
/**
 * @namespace CoreModule
 * @description The **CoreModule** serves as the backbone of the application, organizing and executing foundational logic. 
 * It provides a centralized system for managing interactions, parameter transformations, and application state, 
 * ensuring seamless operation and integration of various components.
 */

/**
 * @namespace InputInterface
 * @description Provides documentation for sensors, MIDI controllers, touch, and other user input mechanisms. 
 * This namespace focuses on capturing, processing, and responding to user interactions efficiently and intuitively.
 */

/**
 * @namespace 2DGUI
 * @description Handles all 2D graphical user interface elements, including sliders, buttons, knobs, and parameter displays. 
 * This namespace integrates WebAudioControls for creating custom, reusable components to control audio parameters seamlessly. 
 * Its focus is on interactive and visually appealing controls optimized for 2D environments.
 */

/**
 * @namespace 3DGUI
 * @description Manages 3D graphical user interface components, including interactive elements within 3D scenes. 
 * It integrates seamlessly with Three.js to provide immersive user interactions.
 */

/**
 * @namespace AudioEngine
 * @description Encapsulates the core logic for audio processing, synthesis, and playback. 
 * This namespace manages the Web Audio API, Tone.js orbiters, and audio parameters to create a dynamic sound environment.
 */
// -----------------------------
// Import Statements
// -----------------------------

// Import scene setup and space elements
import './ui/uiCoreShim.js';

import {
    ensureOscilloscopeOverlay,
    updateOscilloscopePerformanceProfile,
    configureCameraAutomation,
    drawRing,
    isRingEnabled,
    destroyOscilloscopeOverlay,
} from './world/Scene.js';
import { getCameraFocus } from './world/cameraFocus.js';
import { RingOscilloscope } from './world/Ring.js';
import {
  bindPerformanceModeVisibilitySync,
  hydratePerformanceModeFromUserSettings,
  initPerformanceMode,
  isPerformanceModeEnabled,
  subscribePerformanceMode,
} from './config/performanceMode.js';
import { hydrateMidiControlSettings } from './input/midi/midiControlSettings.js';
import { createSceneProfileController } from './world/sceneProfileController.js';
import { applySpaceBackground } from './world/SceneSpace.js';
import { createPerformanceFigure } from './world/performanceFigure.js';

import { WorldSceneController } from './world/WorldSceneController.js';
import { WorldManagerExtended } from './world/WorldManagerExtended.js';
import { OrbiterModeController } from './orbiter/OrbiterModeController.js';
import {
    getWorldInteractionModeFromUrl,
    getSessionInputSourceFromUrl,
    getDirectPayloadFromURL,
    deriveDescriptorFromHydratedPayload,
    getSessionIdFromUrl,
    getFpsOverlayEnabledFromUrl,
} from './utils/urlParams.js';
import {
    resolveGraphicsPreset,
    normalizeGraphicsPresetKey,
    getGraphicsPresetByKey,
    GRAPHICS_PRESETS,
} from './config/performance.js';
import {
    resolveAudioPerformancePreset,
    normalizeAudioPerformanceKey,
    getAudioPerformancePresetByKey,
    persistAudioPerformanceKey,
    getAudioPerformanceThrottleMs,
} from './config/audioPerformance.js';
import { buildEditModeFallback } from './defaults/editModeFallback.js';
import { start as startToneContext } from 'tone';

// Data manager for handling application data
import { DataManager } from './api/dataManager/index.js';
import { trackHasPlayableAudio } from './api/dataManager/normalizers.js';

// Internationalization

// Constants for configuration and defaults
import {
    Constants,
    DEFAULT_PLAY_TRACK_ID,
    DEFAULT_EDIT_TRACK_ID,
    AXIS_ROTATION_CONSTRAINTS,
    setPerformanceThrottleMs,
} from './config/Constants.js';

// Iframe session helpers
import {
    getOrbiterSession,
    updateOrbiterSession,
    setOrbiterPlaybackBridge,
} from './utils/iFrameParams.js';



// Interaction and UI setup
import { createOrbiterParameterEmitter } from './orbiter/emitOrbiterParameters.js';
import { bindViewportHandlers, sendWorldSize } from './ui/viewport.js';
import { deriveDesignDefaultsFromCombined } from './ui/designManager.js';
import { applyBrandVars } from './api/orbiterThemes.js';
import { activateMarkedControlTooltips } from './ui/controlTooltips.js';
import { createSessionManager } from './session/sessionController.js';
import { createPlaybackSessionLoader } from './session/playbackSessionLoader.js';
import { createInitializeBaseFlow } from './orbiter/baseFlow/createInitializeBaseFlow.js';
import { mountGranularVisual } from './visual/granularVisualBridge.js';
import { mountEffectVisuals } from './visual/effectVisualsBridge.js';
import { clearVisualFeedback, subscribeVisualFeedback } from './visual/visualFeedbackSettings.js';
import { resolveEffectVisualSettings } from './visual/effectVisualPolicy.js';
import { sessionDescriptorSignature, sanitizeId } from './session/sessionDescriptor.js';
import {
    createStubOrbiterFromCombined,
    createFallbackStubOrbiter,
    deriveStacksDefaultsFromCombined,
    deriveMappingDefaultsFromCombined,
} from './session/editModeHelpers.js';
import { cloneStacksState } from './core/stackUtils.js';




// Parameter manager for managing adjustable parameters
import { ParameterManager } from './core/ParameterManager.js';
import { PanelManager } from './core/PanelManager.js';
import { CameraController } from './input/CameraController.js';
import { voiceRegistry, PRIMARY_VOICE_ID } from './voice/VoiceRegistry.js';
import { Deck } from './voice/Deck.js';
import { getSharedClockState } from './sync/init.js';
import { TransportControl } from './ui/TransportControl.js';

// Notifications handler
import { createOrbitersUsageEventsClient } from './platform-events/orbitersUsageEvents.js';
import { initTrackLoopPersistence } from './sync/trackSettingsCommit.js';
import { TEMPO_MIN_BPM, TEMPO_MAX_BPM } from './sync/tempoRange.js';
import { initCapture } from './export/capture.js';
import { isCaptureWindow, bootCaptureWindow, isRenderHeldForCapture } from './export/captureWindow.js';


import { createCosmicLfoCoordinator } from './input/cosmicLfoCoordinator.js';
import { MIDIControllerInstance } from './input/midi/MIDIController.js';
import { hideLoadingScreen } from './boot/loadingScreen.js';
import { createLoadProgress } from './boot/loadProgress.js';
import { createMonitorHydration } from './ui/monitorHydration.js';

// Auth sync between shared session cookie and Firebase
import { ensureFirebaseAuthFromSession, isIdentityEstablished } from './auth/sessionAuth.js';
import { expectAudioCacheReconcile, reconcileAudioCacheOwner } from './audio/playback/audioCacheStore.js';
import { expectOwnerReconcile, reconcileOfflineLibraryOwner } from './offline/offlineLibraryStore.js';
import { refreshOfflineSnapshot } from './offline/offlineLibrary.js';

// ...existing code...

/**
 * Construct the Orbiters runtime: builds every singleton, wires the session interceptors and
 * lifecycle handlers, and returns `{ start, parameterManager, voiceId, suspend, resume, dispose }`.
 * Call once, after i18n is ready (the composition root awaits `initI18n()` before constructing the
 * app). `start()` kicks the DOM-ready boot: initial session fetch → React mount → render ring.
 *
 * All options default to today's single-orbiter behavior (so `createOrbitersApp()` with
 * no args is byte-identical). The multi-orbiter composition owner (createMultiOrbiterApp) passes them
 * for the PRIMARY (full) voice:
 * @param {object} [opts]
 * @param {string} [opts.voiceId] the realm-registry key + the engine-registration target voice
 *   (defaults to PRIMARY_VOICE_ID — the single always-active voice).
 * @param {*} [opts.outputNode] a shared master bus (host.getInputNode()) the adapter mixes into,
 *   skipping its own limiter (default null → own limiter → Tone.Destination).
 * @param {*} [opts.transport] a shared transport for the adapter (default null → own
 *   TransportController over its own shared-package Transport — per-voice transport is independent).
 * @param {boolean} [opts.installLifecycle] install this instance's page-lifecycle handlers (default
 *   true; the multi-orbiter shell passes false and owns ONE shared handler set, driving the returned
 *   suspend/resume/dispose).
 * @param {{trackId?: string, orbiterId?: string, entangledWorldId?: string}} [opts.sessionDescriptor]
 *   the entity this voice should boot (default null → resolve from the URL / host bridge as today).
 *   The multi-orbiter shell passes the PRIMARY voice's roster entry so the multi boot honors
 *   roster[0] instead of the URL fallback.
 */
export function createOrbitersApp({
    voiceId = PRIMARY_VOICE_ID,
    outputNode = null,
    transport = null,
    installLifecycle = true,
    sessionDescriptor = null,
    canvasEl = null,
    // In the multi-orbiter realm the ViewportCompositor owns ONE renderer; a scene voice
    // passes it here and its WorldSceneController borrows it (one WebGL context → textures upload for
    // every voice) instead of creating its own. No `sharedRenderer` → single-orbiter is byte-identical.
    sharedRenderer = null,
    mountChrome = true,
    // The DOM cell to mount this voice's React UI into (a grid tile). Null →
    // single-orbiter, mounts to document.body and fills the viewport (byte-identical).
    uiContainer = null,
    // In a multi grid exactly one voice owns the realm-global UI side effects
    // (the `data-ui-react` body attr + `.dark` class). Single-orbiter always owns them.
    isPrimary = false,
    // The voice's session event bus. Single-orbiter defaults to `window` (byte-identical);
    // a multi-orbiter voice passes its OWN EventTarget so session→world signals don't cross-talk between
    // voices (otherwise only one of N planets loads its world texture).
    eventBus = (typeof window !== 'undefined' ? window : null),
    // True when this voice lives in the shared multi-orbiter realm. Drives two policies
    // explicitly (rather than inferring them from bus identity): the voice neither touches the ONE
    // global boot overlay (per-tile overlays own feedback) nor prunes the realm-shared release
    // caches (one voice's load must not evict its siblings' releases). Single-orbiter: false.
    sharedRealm = false,
} = {}) {
const IS_IFRAME = typeof window !== 'undefined' && window.self !== window.top;

// Precompute URL parameters & initial mode
const INITIAL_URL_PARAMS = new URLSearchParams(window.location.search);
// Fixed-size capture window: the same orbiter with the full interface, sized to the chosen
// aspect. Window snapping + auto-record (once the orbiter is loaded) is wired in handleDomReady.
const IS_CAPTURE_WINDOW = isCaptureWindow();
const INITIAL_WORLD_MODE = getWorldInteractionModeFromUrl(INITIAL_URL_PARAMS);
const INITIAL_GRAPHICS_RESOLUTION = resolveGraphicsPreset(INITIAL_URL_PARAMS);
const INITIAL_GRAPHICS_PRESET_KEY = INITIAL_GRAPHICS_RESOLUTION.key;
const INITIAL_GRAPHICS_PROFILE = INITIAL_GRAPHICS_RESOLUTION.preset;
const INITIAL_GRAPHICS_PREFERENCE = INITIAL_GRAPHICS_PROFILE.label;
const INITIAL_AUDIO_RESOLUTION = resolveAudioPerformancePreset(INITIAL_URL_PARAMS);
const INITIAL_AUDIO_PRESET_KEY = INITIAL_AUDIO_RESOLUTION.key;
const INITIAL_AUDIO_PROFILE = INITIAL_AUDIO_RESOLUTION.preset;
const INITIAL_SESSION_SOURCE = getSessionInputSourceFromUrl(INITIAL_URL_PARAMS);
const INITIAL_SESSION_ID = getSessionIdFromUrl(INITIAL_URL_PARAMS);
const INITIAL_FPS_OVERLAY_ENABLED = getFpsOverlayEnabledFromUrl(INITIAL_URL_PARAMS);
// Resolved synchronously from the url (or this device's stored preference) so the FIRST frame is
// already the right look — a boot that draws the full world and switches a moment later would pay
// the whole cost the mode exists to avoid, including the skybox fetch.
const PREFERS_PERFORMANCE_MODE = initPerformanceMode(INITIAL_URL_PARAMS);
const IS_INITIAL_EDIT_MODE = INITIAL_WORLD_MODE === 'edit';
// Editing is exempt, and it is the only exemption. Axis mappings are authored by watching the planet
// move — with no planet you are not in a cheaper mode, you are looking at a screen that cannot show
// you the thing you are changing. The mode exists to protect audio while PLAYING; the preference
// itself is left untouched, so it applies again the moment this session goes back to play.
const PERFORMANCE_MODE_APPLIES = !IS_INITIAL_EDIT_MODE;
const INITIAL_PERFORMANCE_MODE = PERFORMANCE_MODE_APPLIES && PREFERS_PERFORMANCE_MODE;
const usageEvents = createOrbitersUsageEventsClient({
    mode: INITIAL_WORLD_MODE,
    getEngine: () => audioEngine || voiceRegistry.getActive()?.audioEngine || null,
});
usageEvents.start();
// Remember this user's loop (on/off + region) per track — one listener on the shared loop event, so
// every surface that can move a loop persists without having to know about persistence. See
// `initTrackLoopPersistence`. Lives for the session, like the usage-events client above.
initTrackLoopPersistence();

let activeGraphicsProfileKey = INITIAL_GRAPHICS_PRESET_KEY;
let activeGraphicsProfile = INITIAL_GRAPHICS_PROFILE;
// Per-group effect-visual settings, resolved from the graphics profile by the
// one policy resolver. Cached so per-frame readers get a stable object, recomputed on profile change.
//
// Performance mode is the resolver's PREFERENCE input — the layer the policy was written to take an
// off switch on. Turning it off there means every group resolves to `enabled: false`, so no layer is
// built and none of them needed to learn about the mode.
const effectVisualPreference = () => ({ enabled: !isPerformanceModeEnabled() });
let activeEffectVisualSettings = resolveEffectVisualSettings(
    activeGraphicsProfile,
    effectVisualPreference(),
);
let activeAudioProfileKey = INITIAL_AUDIO_PRESET_KEY;
let activeAudioProfile = INITIAL_AUDIO_PROFILE;

// Retrieve the canvas element for 3D rendering. A per-voice canvas (multi-orbiter scene voice)
// may be injected; the single-orbiter default stays the fixed #canvas3D.
//
// With the 3D off a realm builds no compositor, so a voice joining one is handed NO canvas — there
// is none to lend. Inside this app the fallback below still finds the page's own `#canvas3D`, but an
// EMBEDDING host has no such element, so the fallback found nothing and the voice threw before it
// could show anything at all. What is missing there is an element, not a drawing surface: a realm
// voice never renders through its own canvas — the compositor owns the one renderer, and turning the
// 3D back on hands this voice THAT renderer, never this element. So an unattached canvas is the
// honest answer, and only for a realm voice: a lone orbiter really does need a real one, and must
// still say so.
const canvas3D =
    canvasEl
    || document.getElementById('canvas3D')
    || (sharedRealm ? document.createElement('canvas') : null);

// Shared scene controller integrates renderer, camera, controls
const worldController = new WorldSceneController(canvas3D, {
    performanceProfile: INITIAL_GRAPHICS_PROFILE,
    sceneProfile: INITIAL_PERFORMANCE_MODE ? 'off' : 'full',
    showFpsOverlay: INITIAL_FPS_OVERLAY_ENABLED,
    sharedRenderer,
    // Told rather than inferred from `sharedRenderer`. A realm voice that BOOTS with the 3D off is
    // handed no renderer at all, so a voice that guessed from that would decide it was a lone orbiter
    // and go on to build a renderer of its own on the realm's behalf.
    realmVoice: sharedRealm,
    // This voice's ONE camera-input surface = its cell in a shared realm (`uiContainer`), or the
    // app's fullscreen canvas single-orbiter. Always set (never the shared canvas), so the CameraController
    // binds exactly one element it owns.
    inputElement: uiContainer ?? canvas3D,
});
const { scene, camera, renderer } = worldController;

// The ParameterManager is constructed up-front (no singleton) so camera automation,
// edit mode, MIDI, and the cosmic LFOs all receive the SAME per-voice instance via DI. Param
// REGISTRATION (x/y/z, sync-bpm, ...) still happens below — subscribers (camera automation) bind
// to the live manager before the params exist, exactly as the prior singleton timing did.
const parameterManager = new ParameterManager();
// The MIDI controller is a module-level singleton built at import (before any voice); inject the
// voice's manager now and let it create its feedback bridge.
if (MIDIControllerInstance) {
    MIDIControllerInstance.setParameterManager(parameterManager);
}

// Gate this voice's camera orbit on ITS OWN playback state, resolved off the registry each frame (the
// audio engine attaches after its async init). In the multi-stage collection view all voices share one
// realm, so reading the realm-global playback made every camera animate when any one voice played;
// scoping to this voice keeps play/Space animating only the focused (playing) orbiter. Single-orbiter
// registers one always-active voice, so this reads the same state the global would have (equivalent).
configureCameraAutomation(
    worldController,
    parameterManager,
    () => voiceRegistry.get(voiceId)?.audioEngine?.getPlaybackState() ?? 'stopped',
    // What this voice's camera orbits — the world, or its first moon.
    () => getCameraFocus(voiceId),
);

// Construct THIS voice's oscilloscope up-front (no module singleton) and keep the
// reference in this module's closure. It is attached to the VoiceContext at registration below, and
// every boot-time oscilloscope call (perf profile, overlay attach) passes this same instance. Built
// here — before the perf-profile / overlay-attach calls and before the render callback that reads it
// — so there is no swallowed ReferenceError / black screen from touching it before construction.
const oscilloscope = new RingOscilloscope();
updateOscilloscopePerformanceProfile(oscilloscope, INITIAL_GRAPHICS_PROFILE);

if (typeof window !== 'undefined') {
    window.__orbitersGraphicsProfile = {
        key: activeGraphicsProfileKey,
        config: activeGraphicsProfile,
    };
    window.__orbitersAudioProfile = {
        key: activeAudioProfileKey,
        config: activeAudioProfile,
    };
}
syncFeedbackThrottleToAudioProfile(activeAudioProfileKey);
// `from` matters: a key the account's cookie supplied must not be copied into this device's own
// remembered choice — see `persistAudioPerformanceKey`.
persistAudioPerformanceKey(activeAudioProfileKey, { from: INITIAL_AUDIO_RESOLUTION.source });

// Ensure the oscilloscope overlay exists and is attached
ensureOscilloscopeOverlay(oscilloscope, scene);

// Placeholder: instantiate extended manager (world content integration forthcoming)
// `getRenderer` rather than the destructured `renderer` above: that const is the value at BOOT, and
// the controller replaces its renderer on every no-3D toggle and every realm compositor swap. See
// WorldManagerExtended for what a stale one costs.
const worldManager = new WorldManagerExtended(scene, {
    renderer,
    getRenderer: () => worldController.renderer,
});
// Edit-mode emitter — getters resolve the controller + engine lazily (both declared below),
// so this needs no function hoisting and stays a plain const.
// The DOM element this voice's orbiter theme (--color1/2/3, font) is scoped to. For a
// multi tile that's its grid cell, so each tile shows its own world's colors; single-orbiter passes
// null so applyDesignSettings falls back to documentElement (byte-identical, page-wide theme).
const designThemeRoot = uiContainer || null;
// De-singletonization: this voice OWNS its PanelManager (not a module singleton),
// so opening a panel in one tile doesn't move the others. It broadcasts panel changes on this voice's
// eventBus (window for single-orbiter → byte-identical) and scopes its chrome to the voice's root.
const panelManager = new PanelManager({
    eventBus,
    root: uiContainer ?? (typeof document !== 'undefined' ? document : null),
    voiceId,
});
// De-singletonization: this voice OWNS its TransportControl (not a singleton),
// so play/pause in one tile drives only that tile's orbiter. It dispatches transport-state-change on
// this voice's eventBus (window for single-orbiter → byte-identical).
const transportControl = new TransportControl({
    eventBus,
    root: uiContainer ?? (typeof document !== 'undefined' ? document : null),
});
const emitOrbiterParameters = createOrbiterParameterEmitter({
    getModeController: () => orbiterModeController,
    getAudioEngine: () => audioEngine,
    themeRoot: designThemeRoot,
});
// The scene profile's owner is created further down — it needs the oscilloscope and the monitor
// hydration, which do not exist yet. Until it does, the value resolved at boot IS the answer; after
// that every reader asks the owner, so nothing else has to know how the preference is stored.
let sceneProfileOwner = null;
const is3dOff = () => (sceneProfileOwner ? sceneProfileOwner.is3dOff : INITIAL_PERFORMANCE_MODE);

const orbiterModeController = new OrbiterModeController({
    worldManager,
    scene,
    defaultMode: INITIAL_WORLD_MODE,
    emitParameterUpdate: emitOrbiterParameters,
    parameterManager,
    themeRoot: designThemeRoot,
    // Stamp this voice's dimension-changed events (single-orbiter null → byte-identical).
    voiceId,
    is3dOff,
    onModeChanged: (mode) => {}
});

// Instantiate the DataManager for handling track data. Constructed BEFORE bindViewportHandlers
// because that runs an initial resize synchronously, which calls getTrackId() → dataManager (the
// active trackId now lives on the DataManager instance, not a Constants global).
// Does this voice own the ONE global boot overlay? Only the single-orbiter app (whose
// event bus IS window) does. A multi/collection voice gets its own EventTarget, reports progress
// on it (per-tile overlays subscribe there), and must never touch the global overlay — previously
// N voices thrashed the one 4-step counter and the FIRST voice to finish removed the overlay while
// its siblings were still loading behind a black screen.
const ownsGlobalLoadingScreen = !sharedRealm;
const loadProgress = createLoadProgress({ voiceId, eventBus, mirrorGlobal: ownsGlobalLoadingScreen });

// Observable per-voice boot completion — settles `{ok}` when this voice's bootstrap
// finishes (or falls back). The multi shell awaits it (with a deadline) so ONE stuck voice can no
// longer hang the whole realm invisibly; single-orbiter never awaits it.
let resolveWhenReady;
const whenReady = new Promise((resolve) => { resolveWhenReady = resolve; });
function settleReady(result) {
    if (resolveWhenReady) {
        resolveWhenReady(result);
        resolveWhenReady = null;
    }
}

// The descriptor goes in so this voice's release pin (`trackVersion`) survives all the way to
// the release fetch — the shared realm's one page URL cannot carry a per-voice version.
const dataManager = new DataManager({
    eventBus,
    loadProgress,
    sharedRealmCache: sharedRealm,
    sessionDescriptor,
});

// This voice's DECK — the one owner of its sync/warp flags, tempo, meter, grid, and beat clock. The
// coordinator fans each master/status change to it; its shared-clock source reads the realm clock live
// (a function so the beat is never cached). Seeded from trackData by the audio adapter. `collection`
// comes from the CONSTRUCTION PATH (sharedRealm), never the live registry size — sibling voices
// register asynchronously, so size at construction lies. A solo deck mirrors the master and keeps the
// historical follow rule (only while the sync session is enabled), byte-identical.
const deck = new Deck({ voiceId, collection: sharedRealm });
deck.setSharedClockSource(() => getSharedClockState());

// Register this orbiter as the single voice in the realm registry — the realm-global
// replacement for the old `window.__orbitersWorldController/WorldMode` + `window.dataManagerInstance`
// slots that the last-booted orbiter used to clobber. Single boot = one always-active voice, so
// every reader resolves it via `voiceRegistry.getActive()`. The audio engine and cosmic LFOs are
// filled onto this same record below, once they exist (two-phase: sync fields now, async ones later).
// The VOICE owns its camera-input controller — created here at boot (world + params + the voice's
// input surface all exist) and disposed with the voice, not spun up/torn down on every jamming-panel
// enter/exit. Panel state is now just a drive filter (see PanelManager). It focuses this voice on
// pointerdown, so no realm-level hit-test is needed.
const cameraController = new CameraController(worldController, parameterManager, voiceId);

voiceRegistry.register(voiceId, {
    id: voiceId,
    parameterManager,
    worldController,
    // This voice's camera-input controller. Read by PanelManager to gate drive on panel change.
    cameraController,
    worldMode: orbiterModeController,
    dataManager,
    // The voice's resolved trackId (the loaded track wins; the boot descriptor / URL are the pre-load
    // fallback). Read by the capture flow when the engine is embedded in a host page, to target this
    // voice's track in the standalone app.
    getTrackId: () =>
        dataManager.activeConfigRequest?.trackId ??
        sessionDescriptor?.trackId ??
        INITIAL_URL_PARAMS.get('trackId') ??
        null,
    transportControl,
    // Rendering hand-off for capture. Recording opens a second orbiter window, and two live render
    // loops is exactly the cost recording can least afford — so the capture flow walks this registry
    // (the same walk it already does to pause transports) and parks every voice's visuals for the
    // duration. Per voice, so a collection or feed realm hands over every stage, not just the one
    // whose RECORD was pressed.
    suspendVisuals: suspendVisualResources,
    resumeVisuals: resumeVisualResources,
    // This voice's own PanelManager (panel state is per-voice, de-singletonized).
    panelManager,
    // This voice's event channel — the SAME EventTarget the PanelManager dispatches on
    // and the React engine context subscribes to (window for single-orbiter). Registering it here is
    // what lets `resolveEngineContext` find it via `getVoice().eventBus` so dispatch + subscribe match.
    eventBus,
    deck,
    // The voice owns its ring oscilloscope (constructed above, attached to this
    // voice's scene). The render loop and active-voice consumers resolve it off the registry.
    oscilloscope,
    // The voice's DOM root — `document` for single-orbiter (so scoped lookups are
    // byte-identical), a per-voice subtree in multi-orbiter.
    rootEl: typeof document !== 'undefined' ? document : null,
    // The element this voice's orbiter theme (`--color1/2/3`) is written to (its grid cell in
    // multi-orbiter; null in single-orbiter where the theme lands on documentElement directly). The
    // multi shell reads it on focus change to mirror the FOCUSED tile's accent onto documentElement so
    // body-portalled menus (More / Monitor) theme correctly.
    themeRoot: designThemeRoot,
});

// The window-following bits of the fullscreen single-orbiter shell: the nav viewport state and the
// world-size message to the host. Renderer size and camera aspect are NOT here — `worldController`
// owns those and fits them to the canvas box (which edit mode's sheet makes smaller than the window).
// This belongs ONLY to a voice that OWNS its renderer; a shared-renderer voice is sized/framed
// per-cell by the ViewportCompositor, and its host must not be told a fullscreen world size.
// (Gated on the CONSTRUCTION PATH, not `mountChrome` — multi tiles now mount chrome but must not
// reframe. Not on `!sharedRenderer` either: a realm tile booting with the 3D off is handed no
// renderer, and would then mistake itself for the standalone app.)
if (!sharedRealm) {
    bindViewportHandlers({
        renderer,
        getTrackId: () => dataManager.activeConfigRequest?.trackId ?? null,
    });
}

const sessionManager = createSessionManager({
    dataManager,
    updateOrbiterSession,
    eventBus,
});

const playbackLoader = createPlaybackSessionLoader({
    dataManager,
    sessionManager,
    eventTarget: eventBus,
});

if (eventBus) {
    eventBus.addEventListener('orbiters:session-load', interceptPlaybackSessionLoad, { capture: true });
    eventBus.addEventListener('orbiters:session-update', interceptPlaybackSessionUpdate, { capture: true });
}

function syncFeedbackThrottleToAudioProfile(profileKey) {
    const throttleMs = getAudioPerformanceThrottleMs(profileKey);
    setPerformanceThrottleMs(throttleMs);
}

function getLastSessionResolution() {
    if (sessionManager && typeof sessionManager.getLastResolution === 'function') {
        return sessionManager.getLastResolution();
    }
    return null;
}

function refreshDimensionScopedUi() {
    const resolution = getLastSessionResolution();
    if (!resolution || resolution.ok === false) {
        return;
    }

    const entangledWorld = resolution.entangledWorld ?? resolution.track?.entangledWorld ?? null;
    try {
        primeCosmicLfoUi(entangledWorld);
    } catch (error) {
        console.warn('[Main] Failed to refresh cosmic LFO sources after dimension change', error);
    }

    if (dataManager) {
        try {
            if (dataManager.activeView) {
                dataManager.populatePlaceholders(dataManager.activeView);
            } else {
                monitorHydration.hydrate(dataManager);
            }
        } catch (error) {
            console.warn('[Main] Failed to refresh placeholders after dimension change', error);
        }
    }
}

if (typeof document !== 'undefined') {
    if (!document.__orbitersDimensionUiHandler) {
        document.__orbitersDimensionUiHandler = refreshDimensionScopedUi;
        document.addEventListener('orbiters:dimension-changed', refreshDimensionScopedUi, {
            passive: true,
        });
    }
}

// Instantiate the Audio Engine for managing playback
let audioEngine;
let orbiterWarningShown = false;
let lastSessionSignature = null;
let pendingSessionLoad = null;
let lastResolvedSession = null;
let usageEventsPlaybackUnsubscribe = null;

setOrbiterPlaybackBridge({
    getCurrentTime: () => {
        if (!audioEngine || typeof audioEngine.getCurrentPositionMs !== 'function') {
            return 0;
        }
        const positionMs = Number(audioEngine.getCurrentPositionMs());
        return Number.isFinite(positionMs) ? positionMs / 1000 : 0;
    },
    isPlaying: () => {
        if (!audioEngine || typeof audioEngine.isPlaying !== 'function') {
            return false;
        }
        return Boolean(audioEngine.isPlaying());
    },
    seek: async ({ time } = {}) => {
        if (!audioEngine || typeof audioEngine.seekToMilliseconds !== 'function') {
            throw new Error('Audio engine is not ready.');
        }
        const seconds = Number(time);
        if (!Number.isFinite(seconds)) {
            throw new Error('Invalid playback seek time.');
        }
        await audioEngine.seekToMilliseconds(Math.max(0, seconds) * 1000);
    },
});

// Register the adjustable parameters for user 1 (the manager itself is constructed up-front,
// near the camera-automation wiring above — per-voice DI).
const {
    min: ROOT_AXIS_MIN = 0,
    max: ROOT_AXIS_MAX = 1,
    equilibrium: ROOT_AXIS_EQ = 0,
} = AXIS_ROTATION_CONSTRAINTS || {};

['x', 'y', 'z'].forEach((axis) => {
    parameterManager.addParameter(
        axis,
        ROOT_AXIS_EQ,
        ROOT_AXIS_MIN,
        ROOT_AXIS_MAX,
        true,
        'linear',
        (value) => value,
        (value) => value
    );
});

function syncUsageEventsPlaybackListener() {
    if (typeof usageEventsPlaybackUnsubscribe === 'function') {
        usageEventsPlaybackUnsubscribe();
        usageEventsPlaybackUnsubscribe = null;
    }

    if (!audioEngine || typeof audioEngine.addPlaybackStateListener !== 'function') {
        return;
    }

    usageEventsPlaybackUnsubscribe = audioEngine.addPlaybackStateListener((payload = {}) => {
        usageEvents.handlePlaybackStateChange(payload);
    });
}

// The TRANSPORT tempo's range comes from `sync/tempoRange.js` — the same range the deck clamps to,
// so this readout can never pin at a floor while the deck runs past it. The old 20–300 was a UI
// range with nothing behind it; what really limits a deck is its sink's playback rate, which is
// per track. `sync-track-bpm` below is a different quantity — a track's ANALYZED tempo — and keeps
// its own musical 20–300.
parameterManager.addParameter(
    'sync-bpm',
    120,
    TEMPO_MIN_BPM,
    TEMPO_MAX_BPM,
    true,
    'linear',
    (value) => value,
    (value) => value
);

parameterManager.addParameter(
    'sync-track-bpm',
    120,
    20,
    300,
    true,
    'linear',
    (value) => value,
    (value) => value
);

let audioEngineDisposeBound = false;
let visualResourcesSuspended = false;
let lifecycleHandlersInstalled = false;
let instanceDisposed = false;
// This voice's performance-mode subscriptions. The preference module's listener set is module level,
// so these MUST be released on teardown or a removed tile keeps its whole voice graph alive.
let unsubscribePerformanceMode = null;
let unbindPerformanceModeVisibility = null;
// This voice's granular visual bridge (engine lifetime → accretion disk layer). Mounted when the
// audio engine lands on the voice entry; this instance owns its teardown (the single-orbiter path
// never unregisters its voice, so registry events can't be the trigger).
let granularVisualHandle = null;
// This voice's rack-effect visual bridge (effect lifetimes → group layers: echoes on the moons,
// space/air on the cloud shell + glow). Same seam, same ownership as the granular bridge.
let effectVisualsHandle = null;
// The registry subscription that mounts the bridge on engine assignment (released on dispose).
let unsubscribeEngineAssigned = null;
// The visual-feedback subscription: a module's switch moved in the Studio, so the effect bridge
// re-decides what it binds. Only the visuals move — the audio graph is never touched.
let unsubscribeVisualFeedback = null;
// The React-UI shell handle for this instance, so full teardown unmounts its root. Without this a voice
// removed in place (a collection stage cleared / replaced without changing the stage count) would leave
// its UI root rendering against a disposed engine over the now-empty cell.
let reactUIHandle = null;

function suspendVisualResources() {
    if (visualResourcesSuspended || instanceDisposed) {
        return;
    }
    try {
        worldController.setRenderActive?.(false);
    } catch (_) {}
    try {
        destroyOscilloscopeOverlay(oscilloscope);
    } catch (_) {}
    visualResourcesSuspended = true;
}

function resumeVisualResources() {
    // A live capture window owns the realm's rendering: while it is open this voice stays dark even
    // if its tab is re-shown or restored from bfcache. The capture flow lifts the hold itself.
    if (!visualResourcesSuspended || instanceDisposed || isRenderHeldForCapture()) {
        return;
    }
    // Nothing starts a render loop in a page nobody is looking at. Closing the capture window while
    // the source tab sits in the background would otherwise resume every voice behind the user —
    // the very cost this is here to avoid. The visibilitychange/pageshow handlers call this again
    // when the page actually comes forward, so the resume is deferred, not lost.
    if (document.visibilityState === 'hidden') {
        return;
    }
    try {
        ensureOscilloscopeOverlay(oscilloscope, scene);
    } catch (_) {}
    try {
        worldController.setRenderActive?.(true);
    } catch (_) {}
    visualResourcesSuspended = false;
}

function disposeOrbiterInstance({ reason = 'unknown' } = {}) {
    if (instanceDisposed) return;
    instanceDisposed = true;

    // Settle this voice NOW: its in-flight bootstrap bails on `instanceDisposed`, so it will never
    // reach `settleReady` itself, and dispose has already cleared the deadline timer. The realm's boot
    // worker holds a slot on `settled` — without this, removing a voice mid-boot wedges the queue.
    settleReady({ ok: false, disposed: true });

    // Stop render loop and release visual GPU resources first.
    suspendVisualResources();

    try {
        cosmicLFOManager?.disposeAll?.();
    } catch (error) {
        console.warn('[Main] Failed to dispose CosmicLFO manager', { reason, error });
    }

    // Dispose the voice's camera-input controller before its world (it removes a render callback
    // from the world + its pointer listeners from the input surface).
    try {
        cameraController?.dispose?.();
    } catch (error) {
        console.warn('[Main] Failed to dispose camera controller', { reason, error });
    }

    // Tear down the granular visual bridge before its world controller (it removes a render
    // callback + a scene overlay and frees the layer's GPU resources).
    try {
        unsubscribeEngineAssigned?.();
    } catch (_) {}
    unsubscribeEngineAssigned = null;
    try {
        unsubscribeVisualFeedback?.();
    } catch (_) {}
    unsubscribeVisualFeedback = null;
    try {
        unsubscribePerformanceMode?.();
    } catch (_) {}
    unsubscribePerformanceMode = null;
    try {
        unbindPerformanceModeVisibility?.();
    } catch (_) {}
    unbindPerformanceModeVisibility = null;
    // Its canvas and its built geometry leave with the voice. The container does not — it was lent
    // by the realm and outlives every host and every voice in it.
    try {
        performanceFigure?.dispose?.();
    } catch (_) {}
    // The voice's module switches leave with the voice — a later voice reusing this id must boot
    // from its own orbiter's session, never from a stranger's leftovers.
    try {
        clearVisualFeedback(voiceId);
    } catch (_) {}
    try {
        granularVisualHandle?.dispose?.();
    } catch (error) {
        console.warn('[Main] Failed to dispose granular visual bridge', { reason, error });
    }
    granularVisualHandle = null;
    try {
        effectVisualsHandle?.dispose?.();
    } catch (error) {
        console.warn('[Main] Failed to dispose effect visuals bridge', { reason, error });
    }
    effectVisualsHandle = null;

    // The sky, before the renderer that holds it. `worldController.dispose` never walks the scene, so
    // nothing else releases the skybox — and in a realm the renderer it was uploaded to belongs to the
    // compositor and outlives this voice, so what is not given back here is leaked for the session,
    // once per tile removed. This also settles a load still in flight: the background owner is asked
    // for the LAST time here, so faces that arrive afterwards are released instead of being hung on a
    // scene nobody is drawing any more.
    // `.catch`, not `try`: this is async, so a throw arrives as a rejected promise and a `try` around
    // it would catch nothing while the failure surfaced as an unhandled rejection with no context.
    void applySpaceBackground(scene, { enabled: false }).catch((error) => {
        console.warn('[Main] Failed to release the scene background', { reason, error });
    });

    try {
        worldController.dispose?.();
    } catch (error) {
        console.warn('[Main] Failed to dispose world controller', { reason, error });
    }

    try {
        audioEngine?.dispose?.();
    } catch (error) {
        console.warn('[Main] Failed to dispose audio engine', { reason, error });
    }
    audioEngine = null;

    // Unmount this instance's React-UI root (see reactUIHandle). Removing a voice in place otherwise
    // leaves a live root over the cell; on a real page unload it's harmless but still the correct teardown.
    try {
        reactUIHandle?.unmount?.();
    } catch (error) {
        console.warn('[Main] Failed to unmount React UI shell', { reason, error });
    }
    reactUIHandle = null;
}

function installInstanceLifecycleHandlers() {
    if (lifecycleHandlersInstalled || typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }
    lifecycleHandlersInstalled = true;

    const onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
            suspendVisualResources();
        } else {
            resumeVisualResources();
            // Resume AudioContext if the browser suspended it while the tab was in the background.
            // Tone.start() is safe to call repeatedly — it's a no-op when the context is already running.
            try { startToneContext(); } catch {}
            void audioEngine?.resumeAfterInterruption?.();
        }
    };

    const onPageHide = (event) => {
        suspendVisualResources();
        if (event?.persisted) {
            return;
        }
    };

    const onBeforeUnload = () => {
        disposeOrbiterInstance({ reason: 'beforeunload' });
    };

    const onUnload = () => {
        disposeOrbiterInstance({ reason: 'unload' });
    };

    const onPageShow = (event) => {
        if (event?.persisted) {
            resumeVisualResources();
        }
        try { startToneContext(); } catch {}
        void audioEngine?.resumeAfterInterruption?.();
    };

    document.addEventListener('visibilitychange', onVisibilityChange, { passive: true });
    window.addEventListener('pagehide', onPageHide, { passive: true });
    window.addEventListener('pageshow', onPageShow, { passive: true });
    window.addEventListener('beforeunload', onBeforeUnload, { once: true });
    window.addEventListener('unload', onUnload, { once: true });
}

// The multi-orbiter shell owns ONE shared set of lifecycle handlers and drives the
// returned suspend/resume/dispose; a per-voice install would double-register (and the {once:true}
// beforeunload would dispose only one voice). Single-orbiter keeps installing its own.
if (installLifecycle) {
    installInstanceLifecycleHandlers();
}

// -----------------------------
// Application Initialization Function
// -----------------------------

/**
 * Initializes the application.
 * Fetches track data, sets up the scene, and configures interactions.
 * @async
 * @function initializeApp
 * @throws Will log an error if initialization fails.
 * @memberof CoreModule
 */
async function initializeApp() {
    try {
        const fallbackTrackId = INITIAL_WORLD_MODE === 'edit'
            ? DEFAULT_EDIT_TRACK_ID
            : DEFAULT_PLAY_TRACK_ID;

        try {
            const viewer = await ensureFirebaseAuthFromSession();
            // Whether that answer means anything. A null viewer is "nobody is signed in" when the
            // server said so, and "we could not ask" when there was no server to ask — and only
            // the first is a reason to empty somebody's downloads. See `isIdentityEstablished`.
            const identityEstablished = isIdentityEstablished(viewer);
            // Standalone has no logout to listen for — root's session cookie is httpOnly and
            // there is no channel across the origins — so boot is where a changed identity gets
            // noticed, BEFORE anything this viewer plays is written under the previous owner.
            // Skipped in the embed, where a boot always reads as anonymous until the host sends
            // its token; there, clearing rides on the host clearing that token instead.
            if (!IS_IFRAME) {
                // NOT awaited. The rule is "reconcile before anything reads or writes those bytes",
                // not "before the app starts" — and awaiting it put an IndexedDB open on the path to
                // the first orbiter appearing, so a slow one delayed every load. The gate now lives
                // on the bucket itself (`expectAudioCacheReconcile`), which is the door every read
                // and write goes through.
                expectAudioCacheReconcile();
                void reconcileAudioCacheOwner(viewer?.uid ?? null, { identityEstablished }).catch(() => {});
                // The downloaded library is per uid for the same reason the bucket is, and is
                // reconciled separately rather than as a consequence: either store can fail to open
                // on its own, and a library that survived because the ledger did not would hand the
                // next person a list of someone else's collections.
                //
                // NOT awaited. Nothing on screen before the first frame can read the library — the
                // download surface arrives with the collection drawer, long after this — and the
                // reconcile is the store's own first open, which is the one call that can take a
                // while. Awaiting it put an IndexedDB open on the critical path to the app existing
                // at all. The snapshot behind it only feeds a playback hint, and a voice booted
                // before it lands streams instead of buffering, which is the old behaviour.
                // Declared synchronously, so any library read that happens while the reconcile is
                // still in flight waits for it rather than briefly showing the previous owner's
                // collections. The reconcile itself stays off the critical path.
                expectOwnerReconcile();
                void reconcileOfflineLibraryOwner(viewer?.uid ?? null, { identityEstablished })
                    .then(() => refreshOfflineSnapshot())
                    .catch(() => {});
            }
        } catch (authError) {
            console.warn('[APP] Firebase session sync skipped:', authError?.message || authError);
        }

        const initialTrackId =
            sessionDescriptor?.trackId || INITIAL_URL_PARAMS.get('trackId') || fallbackTrackId;
        await requestInitialSession({ fallbackTrackId: initialTrackId });
    } catch (error) {
        console.error('[APP] Error during application initialization:', error);
        await handleSessionError({ detail: { error } });
    }
}
  
// -----------------------------
// DOMContentLoaded Event Listener
// -----------------------------

/**
 * Executes when the DOM is fully loaded.
 * Initializes the application and starts the animation loop.
 * @memberof CoreModule 
 */
let domReadyHandled = false;
function handleDomReady() {
    if (domReadyHandled) {
        return;
    }
    domReadyHandled = true;

    // Global brand primitives + entity accents, applied once at :root (theme-independent).
    void applyBrandVars();

    initializeApp().then(() => {
        worldController.addRenderCallback(() => {
            // Performance mode bypasses the orbit graph at the ROOT of the per-frame path, before the
            // voice walk and before `getAmplitude()`. Stopping at the draw call would still leave the
            // analyser read running every frame for a picture nobody is going to see — and reading the
            // amplitude is the expensive half, not the line.
            if (isPerformanceModeEnabled()) {
                return;
            }
            // Draw each voice's own oscilloscope from its own audio engine's
            // amplitude. Single-orbiter = one voice, so this is the exact same per-frame draw call as
            // before (its oscilloscope, its audioEngine). Every guard is preserved per-voice: the
            // ring-enabled check, the finite-amplitude coercion, and the one-shot orbiterWarningShown.
            // Index loop over the registry's cached list — no per-frame allocation in this 60fps path.
            const voices = voiceRegistry.all();
            for (let i = 0; i < voices.length; i++) {
                const voice = voices[i];
                const voiceOscilloscope = voice.oscilloscope;
                if (!isRingEnabled(voiceOscilloscope)) {
                    continue;
                }

                let currentAmp = 0;
                const voiceAudioEngine = voice.audioEngine;
                if (voiceAudioEngine) {
                    if (typeof voiceAudioEngine.getAmplitude === 'function') {
                        const amplitude = voiceAudioEngine.getAmplitude();
                        currentAmp = Number.isFinite(amplitude) ? amplitude : 0;
                    } else if (!orbiterWarningShown) {
                        orbiterWarningShown = true;
                    }
                } else if (!orbiterWarningShown) {
                    orbiterWarningShown = true;
                }

                drawRing(voiceOscilloscope, currentAmp);
            }
        });
    });

    activateMarkedControlTooltips();
    sendWorldSize(renderer, dataManager.activeConfigRequest?.trackId ?? null);
    initCapture();
    // Only the STANDALONE single-orbiter app arms its own capture window here. A shared-realm voice
    // (multi / collection tile) must not — every tile runs this boot, so N tiles would each lock the
    // window and race to auto-record. The realm arms capture ONCE at the shell level
    // (`createMultiOrbiterApp`). The test is the construction path: a realm tile booting with the 3D
    // off is handed no renderer, so `!sharedRenderer` would let every tile through here.
    if (IS_CAPTURE_WINDOW && !sharedRealm) {
        bootCaptureWindow();
    }
}

// Kick the boot once the DOM is ready. Called by the composition root after construction.
function start() {
    if (typeof document !== 'undefined') {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                handleDomReady();
            }, { once: true });
        } else {
            handleDomReady();
        }
    }
}


// Cosmic LFOs (x/y/z) + world→sources priming live in the coordinator. Called here to preserve LFO
// construction timing; the manager is registered onto the active voice for runtime readers.
const { manager: cosmicLFOManager, primeCosmicLfoUi } = createCosmicLfoCoordinator(parameterManager, eventBus, { voiceId });
// Attach to THIS voice (not getActive()) so a later-registered voice can't misroute it.
const ownVoice = voiceRegistry.get(voiceId);
if (ownVoice) {
    ownVoice.cosmicLFOManager = cosmicLFOManager;
}

const monitorHydration = createMonitorHydration(
    INITIAL_PERFORMANCE_MODE
        ? GRAPHICS_PRESETS.LOW.uiMonitorThrottleMs
        : activeGraphicsProfile.uiMonitorThrottleMs,
);

// What this voice draws where its planet was, with the 3D off. The container is this voice's own
// box — its cell in a realm, the canvas frame single-orbiter — never the shared realm canvas, which
// belongs to every voice at once. Constructed for every voice, drawn only in the mode: it holds
// nothing until it is enabled, so a session that never enters the mode never builds a figure.
const performanceFigure = createPerformanceFigure({
    container: uiContainer ?? canvas3D?.parentElement ?? null,
});

// One owner for "this voice has 3D / does not". It subscribes to the preference, so every orbiter in
// the window switches from a single toggle, and a background tab catches up on focus.
const sceneProfileController = createSceneProfileController({
    worldController,
    scene,
    oscilloscope,
    monitorHydration,
    getGraphicsProfile: () => activeGraphicsProfile,
    // The world's CONTENTS. The seam sequences these against the renderer's own lifecycle — see its
    // file comment for why the order cannot be swapped. Play only: an edit-mode session keeps its 3D
    // (there is nothing to author without it), so neither hook has anything to do there.
    shedWorld: async () => {
        if (orbiterModeController.activeModeKey !== 'play') return;
        await orbiterModeController.activeMode?.deactivate?.(orbiterModeController.lastContext ?? {});
    },
    rebuildWorld: async () => {
        if (orbiterModeController.activeModeKey !== 'play') return;
        // In a realm the renderer arrives from the compositor, which the realm rebuilds first. If it
        // somehow has not, say so rather than building a world into nothing — the moons would bind
        // their textures to a context that does not exist and fail somewhere much less obvious.
        if (!worldController.renderer) {
            console.warn('[orbitersApp] 3D rebuild skipped — no renderer yet');
            return;
        }
        // (The world manager used to be re-bound to the new renderer here. It now reads the
        // controller's renderer live on every build, so there is nothing to tell it — and this was
        // only ever a fix for THIS path, which is why a realm voice, that rebuilds by another route,
        // still went to the loaders holding the renderer it was constructed with.)
        // `setMode` with the mode already active is the existing refresh path — the world comes back
        // from its own config, and the manager disposes the outgoing one on the way.
        await orbiterModeController.setMode('play');
    },
    // The figure and the planet share the centre, so the seam that owns the switch owns which of
    // them is up. Editing never reaches here (see PERFORMANCE_MODE_APPLIES), so an author keeps the
    // planet they are mapping an axis to.
    setFigureVisible: (visible) => performanceFigure.setEnabled(visible),
});
sceneProfileOwner = sceneProfileController;
// The first apply is what puts the voice into its boot state — including the background, which is
// the skybox with 3D and black (six faces never fetched) without it. Setting the background here as
// well as at boot would be two writers of the same thing.
void sceneProfileController.apply(INITIAL_PERFORMANCE_MODE);
// Held so this voice's teardown releases them. The preference module's listener set is MODULE level
// and outlives any one voice: a tile removed from a collection or room would otherwise leave its
// callback in there holding the whole voice graph — scene, controller, oscilloscope, mode controller
// — and the next toggle would run against a disposed voice.
unsubscribePerformanceMode = subscribePerformanceMode((enabled) => {
    if (instanceDisposed) return;
    // Editing ignores the preference for this session — see PERFORMANCE_MODE_APPLIES. Without this a
    // toggle in another tab would reach in through the shared preference and take the planet away
    // from someone in the middle of mapping an axis to it.
    if (!PERFORMANCE_MODE_APPLIES) return;
    // Disposed, not hidden: the layers hold canvases, scene objects and a full-frame pass, and a
    // layer left standing with nothing driving it still costs what it costs. This runs BEFORE the
    // seam in BOTH directions, because the pass holds a render target on the renderer the seam is
    // about to destroy — and on the way back there is nothing standing to take down anyway.
    teardownEffectVisuals();
    // The seam owns both halves of the switch — shedding the world and giving up the renderer on the
    // way in, building the renderer and the world back on the way out. A rebuild kicked off from here
    // as well would be a second writer racing the first.
    sceneProfileController
        .apply(enabled)
        .then(() => {
            if (instanceDisposed) return;
            // AFTER the seam, never before. These layers draw INTO the 3D — the full-frame pass asks
            // the renderer for its drawing buffer the moment it is built — so mounting them while the
            // renderer is still being rebuilt throws, and the preference module catches each listener
            // separately, which means the throw would be swallowed and the whole switch would simply
            // stop half-done with no error anywhere.
            activeEffectVisualSettings = resolveEffectVisualSettings(
                activeGraphicsProfile,
                effectVisualPreference(),
            );
            mountEffectVisualLayers(null);
        })
        .catch((error) => console.warn('[orbitersApp] performance-mode switch failed', error));
});
unbindPerformanceModeVisibility = bindPerformanceModeVisibilitySync();
// The account-level preference lands after boot and only matters when nothing local already said.
// The account preference lands after boot and only matters when nothing local already said. If it
// disagrees, the subscriber above switches the scene then — the same live path a toggle uses.
void hydratePerformanceModeFromUserSettings();
// Same shape for the MIDI control preferences (takeover mode, autofocus): this device's values are
// already in force from module load, and the account corrects them if it disagrees.
void hydrateMidiControlSettings();

const initializeBaseFlow = createInitializeBaseFlow({
    voiceId,
    loadProgress,
    orbiterModeController,
    // Same reason as the world manager above: the boot-time renderer is not the one that will draw
    // this card. Every card load re-enters this flow, so it must ask again each time.
    getRenderer: () => worldController.renderer,
    scene,
    dataManager,
    parameterManager,
    primeCosmicLfoUi,
    hydrateMonitorPanels: monitorHydration.hydrate,
    getAudioEngine: () => audioEngine,
    setAudioEngine: (engine) => {
        audioEngine = engine;
    },
    getAudioEngineDisposeBound: () => audioEngineDisposeBound,
    setAudioEngineDisposeBound: (value) => {
        audioEngineDisposeBound = value;
    },
    isIframe: IS_IFRAME,
    themeRoot: designThemeRoot,
    panelManager,
    transportControl,
    // The per-voice event bus (window for single-orbiter) the AudioEngineAdapter mirrors
    // its count-in onto, matching the bus the React Transport surface subscribes to.
    eventBus,
});

// The voice whose effect visuals are mounted, remembered so the performance-mode toggle can take
// them down and put them back without waiting for the engine to be reassigned.
let effectVisualsVoiceEntry = null;

function teardownEffectVisuals() {
    granularVisualHandle?.dispose?.();
    granularVisualHandle = null;
    effectVisualsHandle?.dispose?.();
    effectVisualsHandle = null;
}

/**
 * Mount this voice's effect visual layers — the granular accretion disk, the echoes on the moons,
 * the reverb's smear pass, the surface wobble.
 *
 * Performance mode does not mount them at all. They are the layers that draw the SOUND, and each one
 * is a canvas, a scene object or a full-frame pass fed every time a grain fires or a wet meter moves.
 * They were built to be affordable, not free, and on the machines this mode exists for they are the
 * difference. The gate is the resolver's own preference input, so the layers themselves are untouched
 * and each one simply never comes into being.
 */
function mountEffectVisualLayers(voiceEntry) {
    effectVisualsVoiceEntry = voiceEntry ?? effectVisualsVoiceEntry;
    // `PERFORMANCE_MODE_APPLIES` first, because editing is exempt from the mode: an author who has
    // the preference on and opens edit gets the full world, and must not silently lose the layers
    // they are there to author. The live toggle already returns before reaching here in edit; this is
    // the boot path, which used to disagree with it.
    if (!effectVisualsVoiceEntry || (PERFORMANCE_MODE_APPLIES && isPerformanceModeEnabled())) return;
    // The renderer, not just the preference. These layers draw into the 3D and one of them asks the
    // renderer for its drawing buffer as it is built, so "the preference now says the 3D is allowed"
    // is not the same question as "the renderer exists yet" — during a rebuild the first is true
    // while the second is not.
    if (!worldController.renderer) return;
    granularVisualHandle = mountGranularVisual(effectVisualsVoiceEntry, {
        getVisualSettings: () => activeEffectVisualSettings,
    });
    effectVisualsHandle = mountEffectVisuals(effectVisualsVoiceEntry, {
        getVisualSettings: () => activeEffectVisualSettings,
    });
}

// Mount this voice's engine-lifetime consumers (the granular visual bridge) at the registry's
// assignment seam — the one point where the adapter identity is settled on the entry. The
// registry notifies for EVERY voice, so filter to this instance's own voice.
unsubscribeEngineAssigned = voiceRegistry.onAudioEngineAssigned((voiceEntry) => {
    if (voiceEntry?.id !== voiceId) return;
    // A rebuilt engine (fresh adapter identity) gets a fresh bridge; the old
    // one is torn down with the old adapter.
    teardownEffectVisuals();
    mountEffectVisualLayers(voiceEntry);
});

// A module's visual switch moved: the bridge re-decides what it binds — nothing else. The rack, the
// effect nodes and the sound go on exactly as they were.
unsubscribeVisualFeedback = subscribeVisualFeedback((changedVoiceId) => {
    if (changedVoiceId !== voiceId) return;
    effectVisualsHandle?.refresh?.();
});

function buildBootstrapSessionDescriptor({ fallbackTrackId }) {
    const snapshot = getOrbiterSession();
    const requested = { ...(snapshot?.requested || {}) };
    const resolved = snapshot?.resolved ? { ...snapshot.resolved } : null;

    // A multi-orbiter primary boots its roster entry (sessionDescriptor); single-orbiter
    // (sessionDescriptor null) resolves from the URL exactly as before.
    const urlTrackId = sessionDescriptor?.trackId || INITIAL_URL_PARAMS.get('trackId');
    const urlOrbiterId =
        sessionDescriptor?.orbiterId
        || INITIAL_URL_PARAMS.get('orbiterId')
        || INITIAL_URL_PARAMS.get('engineId');
    const urlWorldId =
        sessionDescriptor?.entangledWorldId
        || INITIAL_URL_PARAMS.get('entangledWorldId')
        || INITIAL_URL_PARAMS.get('worldId');

    if (urlTrackId) {
        requested.trackId = urlTrackId;
    }
    if (urlOrbiterId) {
        requested.orbiterId = urlOrbiterId;
    }
    if (urlWorldId) {
        requested.entangledWorldId = urlWorldId;
    }
    // The pin rides the descriptor for a realm voice, the URL for the standalone app. A voice
    // that names `trackVersion` at all is authoritative — including an explicit null, which is
    // how a card says "back to the live release" and must clear an inherited pin.
    const voicePinsVersion =
        sessionDescriptor != null && Object.hasOwn(sessionDescriptor, 'trackVersion');
    const trackVersion = voicePinsVersion
        ? sessionDescriptor.trackVersion
        : INITIAL_URL_PARAMS.get('trackVersion');
    if (trackVersion != null && trackVersion !== '') {
        requested.trackVersion = trackVersion;
    } else if (voicePinsVersion) {
        // Explicitly unpinned by the voice: drop any pin carried in the session snapshot.
        delete requested.trackVersion;
    }
    // Otherwise leave `requested.trackVersion` as the snapshot had it — a standalone boot with
    // no pin of its own must not erase a version the existing session already resolved.

    const baseTrackId = urlTrackId || fallbackTrackId;
    requested.trackId = baseTrackId;
    if (!urlOrbiterId) {
        requested.orbiterId = null;
    }
    if (!urlWorldId) {
        requested.entangledWorldId = null;
    }
    if (resolved && baseTrackId && resolved.trackId !== baseTrackId) {
        resolved.trackId = baseTrackId;
        resolved.orbiterId = urlOrbiterId || null;
        resolved.entangledWorldId = urlWorldId || null;
    }

    return {
        requested,
        resolved,
        source: snapshot?.source || 'app-init',
    };
}

async function bootstrapFromResolution(detail) {
    // A voice removed mid-boot must not build anything: its session resolve is still in flight and
    // resolves onto a disposed instance (world + audio engine + UI would all leak into a dead cell).
    if (instanceDisposed) return;
    if (!detail || !detail.resolution || detail.resolution.ok === false) {
        const error = detail?.resolution?.error || detail?.error || new Error('Invalid session resolution payload');
        throw error;
    }

    const previousResolution = lastResolvedSession;

    const { resolution, descriptor } = detail;
    const changedHints = new Set(
        Array.isArray(detail.changed)
            ? detail.changed
                  .map((entry) =>
                      (typeof entry === 'string' ? entry.trim() : String(entry ?? '')).trim(),
                  )
                  .filter(Boolean)
            : [],
    );
    let combined = {
        track: resolution.track ?? null,
        orbiter: resolution.orbiter ?? null,
        entangledWorld: resolution.entangledWorld ?? null,
        trackUserSettings:
            resolution.trackUserSettings
            ?? resolution.combined?.trackUserSettings
            ?? null,
        // No provenance is seeded: `attachTrackUserSettings` below is the only thing that has read
        // the personal layer, so it is the only thing that may say where it came from. The snapshot
        // cached in between says nothing, and the save guard reads that as "not read yet".
    };

    // The world-into-track nesting now happens inside the assembler
    // (`nestEntangledWorldIntoTrack` at the combined-build), so `resolution.track`
    // already carries `track.entangledWorld` on every path — Main.js no longer re-patches.

    const trackId =
        combined.track?.trackId ||
        descriptor?.trackId ||
        (INITIAL_WORLD_MODE === 'edit' ? DEFAULT_EDIT_TRACK_ID : DEFAULT_PLAY_TRACK_ID);

    if (trackId) {
        Constants.setTrackData(trackId, combined);
    }

    combined = await dataManager.attachTrackUserSettings(combined, trackId);
    if (instanceDisposed) return;

    if (trackId) {
        Constants.setTrackData(trackId, combined);
    }

    const designDefaults = deriveDesignDefaultsFromCombined(combined, {});
    const mappingDefaults = deriveMappingDefaultsFromCombined(combined, {});
    const stacksDefaults = deriveStacksDefaultsFromCombined(combined, null);
    const stackSelection =
        combined.orbiter?.stackSelection ||
        combined.orbiter?.selection ||
        null;

    const stubOrbiter = createStubOrbiterFromCombined(combined, { stacksDefaults });
    const hasPlayableAudio = trackHasPlayableAudio(combined.track);

    const worldModeKey = INITIAL_WORLD_MODE;
    const graphicsPreference = descriptor?.graphicsPreference || INITIAL_GRAPHICS_PREFERENCE;
    const graphicsProfileKey =
        normalizeGraphicsPresetKey(graphicsPreference) || INITIAL_GRAPHICS_PRESET_KEY;
    const graphicsProfile =
        graphicsProfileKey === INITIAL_GRAPHICS_PRESET_KEY
            ? INITIAL_GRAPHICS_PROFILE
            : getGraphicsPresetByKey(graphicsProfileKey);

    const descriptorAudioProfileKey = normalizeAudioPerformanceKey(descriptor?.audioProfile);
    const descriptorNamedAudioProfile = Boolean(descriptorAudioProfileKey);
    const audioProfileKey = descriptorAudioProfileKey || activeAudioProfileKey;
    const audioProfile =
        audioProfileKey === activeAudioProfileKey
            ? activeAudioProfile
            : getAudioPerformancePresetByKey(audioProfileKey);

    if (graphicsProfileKey !== activeGraphicsProfileKey) {
        worldController.updatePerformanceProfile(graphicsProfile);
        updateOscilloscopePerformanceProfile(oscilloscope, graphicsProfile);
    }
    activeGraphicsProfileKey = graphicsProfileKey;
    activeGraphicsProfile = graphicsProfile;
    activeEffectVisualSettings = resolveEffectVisualSettings(
        graphicsProfile,
        effectVisualPreference(),
    );
    // Performance mode's cadence outranks the graphics quality's here. This runs on every resolution
    // event, so writing the quality cadence unconditionally would silently undo the mode's cheapest
    // cadence for the rest of the session — the scene profile is applied once at boot and is
    // idempotent, so it never gets a chance to re-assert it.
    monitorHydration.setIntervalMs(
        isPerformanceModeEnabled()
            ? GRAPHICS_PRESETS.LOW.uiMonitorThrottleMs
            : graphicsProfile.uiMonitorThrottleMs,
    );
    if (typeof window !== 'undefined') {
        window.__orbitersGraphicsProfile = {
            key: activeGraphicsProfileKey,
            config: activeGraphicsProfile,
        };
        window.__orbitersAudioProfile = {
            key: audioProfileKey,
            config: audioProfile,
        };
    }

    activeAudioProfileKey = audioProfileKey;
    activeAudioProfile = audioProfile;
    syncFeedbackThrottleToAudioProfile(audioProfileKey);
    // Say where this key came from, exactly as the boot call does. Without it the account's cookie
    // value gets written into this device's remembered choice here instead — this runs on every
    // track resolution, so the boot guard would be undone moments after it did its job. A key the
    // DESCRIPTOR named is a genuine per-card instruction and is the device's to remember.
    persistAudioPerformanceKey(audioProfileKey, {
        from: descriptorNamedAudioProfile ? 'descriptor' : INITIAL_AUDIO_RESOLUTION.source,
    });

    const worldModeArgs = {
        trackData: combined,
        graphicsPreference,
        graphicsProfile,
        audioProfile,
        designDefaults,
        mappingDefaults,
        stacksDefaults,
        stubOrbiter,
    };

	    const audioEngineOptions = {
	        trackData: combined,
	        engineConfig: stubOrbiter?.orbiterData,
	        userManager: parameterManager,
	        performanceProfile: audioProfile,
	        // Null in single-orbiter (adapter owns its limiter → Destination); the shared
	        // master bus + shared transport when this voice is part of a multi-orbiter view.
	        outputNode,
	        transport,
	        // This voice's deck — the adapter seeds it from trackData and reads tempo/sync from it.
	        deck,
    };

    const previousTrackId =
        previousResolution?.track?.trackId ??
        previousResolution?.request?.trackId ??
        null;
    const previousTrackVersion =
        previousResolution?.track?.version ??
        previousResolution?.request?.trackVersion ??
        null;
    const previousOrbiterId =
        previousResolution?.orbiter?.orbiterId ??
        previousResolution?.request?.orbiterId ??
        null;
    const previousOrbiterVersion =
        previousResolution?.orbiter?.version ??
        previousResolution?.request?.orbiterVersion ??
        null;
    const previousWorldId =
        previousResolution?.entangledWorld?.worldId ??
        previousResolution?.request?.entangledWorldId ??
        null;
    const previousWorldVersion =
        previousResolution?.entangledWorld?.version ??
        previousResolution?.request?.entangledWorldVersion ??
        null;

    const nextTrackId = combined.track?.trackId ?? descriptor?.trackId ?? null;
    const nextTrackVersion = combined.track?.version ?? descriptor?.trackVersion ?? null;
    const nextOrbiterId = combined.orbiter?.orbiterId ?? descriptor?.orbiterId ?? null;
    const nextOrbiterVersion = combined.orbiter?.version ?? descriptor?.orbiterVersion ?? null;
    const nextWorldId = combined.entangledWorld?.worldId ?? descriptor?.entangledWorldId ?? null;
    const nextWorldVersion =
        combined.entangledWorld?.version ?? descriptor?.entangledWorldVersion ?? null;

    const trackChanged =
        !previousResolution ||
        previousTrackId !== nextTrackId ||
        (previousTrackVersion ?? null) !== (nextTrackVersion ?? null);

    const orbiterChanged =
        trackChanged ||
        changedHints.has('orbiterRelease') ||
        changedHints.has('orbiterSession') ||
        previousOrbiterId !== nextOrbiterId ||
        (previousOrbiterVersion ?? null) !== (nextOrbiterVersion ?? null);

    const worldHintChanged =
        changedHints.has('entangledWorldRelease') ||
        changedHints.has('entangledWorldSession') ||
        changedHints.has('entangledWorld') ||
        changedHints.has('entangledWorldId') ||
        changedHints.has('world') ||
        changedHints.has('worldId');

    const worldChanged =
        worldHintChanged ||
        previousWorldId !== nextWorldId ||
        (previousWorldVersion ?? null) !== (nextWorldVersion ?? null);

    const reuseAudioEngine =
        Boolean(audioEngine) && !trackChanged && !orbiterChanged && worldChanged;

    // The figure's inputs: this track's narration section, and the world release it is drawn from.
    // Both are already here — the section rides on the track's own metadata and the world release
    // carries its authored elementals whole — so this is a read, not another fetch. Handed over on
    // every session change; one that leaves the world and the section alone rebuilds nothing.
    performanceFigure.setSource(combined);

    const baseFlowResult = await initializeBaseFlow({
        worldModeKey,
        worldModeArgs,
        trackData: combined,
        entangledWorld: combined.entangledWorld,
        audioEngineOptions,
        skipAudioPreload: worldModeKey === 'edit' ? !hasPlayableAudio : false,
        mappingDefaults,
        stacksDefaults,
        stackSelection,
        designDefaults,
        reuseAudioEngine,
    });

    if (instanceDisposed) return;

    syncUsageEventsPlaybackListener();

    lastResolvedSession = resolution;

    loadProgress.setStep('uiReady', true);
    if (ownsGlobalLoadingScreen) {
        hideLoadingScreen();
    }

    // React UI shell — the only UI. Mounted here, after the engine + root params
    // (incl. premix-deck-i) are ready.
    await ensureReactUIMounted();
    // `audioReady` travels with BOTH the progress event and the readiness promise because a caller
    // may need to know the voice came up WITH sound — a shared session must not report a silent
    // deck as ready to its peers. The promise settles once per voice, so a later in-place swap on
    // that voice reads the per-cycle progress event instead.
    loadProgress.done({ audioReady: baseFlowResult?.engineReady !== false });
    settleReady({ ok: true, audioReady: baseFlowResult?.engineReady !== false });
}

async function handleSessionReady(event) {
    // The session resolve that raised this is not cancellable, so it can land after the voice was
    // removed from its stage. A disposed instance does nothing.
    if (instanceDisposed) return;
    const detail = event?.detail || {};
    // Voice isolation is BUS privacy, not a descriptor filter: each multi voice owns a private
    // `eventBus` (an EventTarget of its own), so another voice's session events never reach this
    // handler; single-orbiter listens on `window` but is the only voice there. A construction-time
    // trackId filter here would wrongly drop a legitimate in-place track swap (the studio can load a
    // different track onto a live voice), so no such guard exists — `sessionDescriptor` only seeds
    // the boot and getTrackId.
    usageEvents.handleSessionReady(detail);
    const signature = detail.descriptor ? sessionDescriptorSignature(detail.descriptor) : null;

    if (signature && signature === lastSessionSignature && detail.cached) {
        return;
    }

    const run = async () => {
        await bootstrapFromResolution(detail);
        if (signature) {
            lastSessionSignature = signature;
        }
    };

    if (pendingSessionLoad) {
        pendingSessionLoad = pendingSessionLoad.then(run, run);
    } else {
        pendingSessionLoad = run();
    }

    try {
        await pendingSessionLoad;
    } catch (error) {
        console.error('[APP] Failed to bootstrap session:', error);
        // A resolved session whose bootstrap throws (e.g. the world GLB 404s after
        // retries) must settle this voice NOW — otherwise the tile spins for the full boot
        // deadline and holds a realm boot slot for nothing.
        loadProgress.fail(error);
        settleReady({ ok: false, error });
    } finally {
        pendingSessionLoad = null;
    }
}

async function handleSessionError(event) {
    // Same in-flight-resolve race as handleSessionReady, and worse: this path bootstraps the edit-mode
    // FALLBACK (a stub orbiter with its own theme), so a dead voice would paint a second, differently
    // coloured interface into a stage the user already cleared.
    if (instanceDisposed) return;
    const detail = event?.detail || {};
    usageEvents.handleSessionFailed(detail);
    const descriptor = detail.descriptor || {};
    const error = detail.error || detail;
    console.error('[APP] Session resolution failed:', error, descriptor);
    // Surface the failure on this voice's channel (multi tile shows an error state).
    // If the fallback bootstrap below completes, the progress steps complete too and the tile
    // overlay clears itself — the error state is only terminal when recovery also fails.
    loadProgress.fail(error);

    lastSessionSignature = null;
    pendingSessionLoad = null;
    sessionManager.resetSignature();
    lastResolvedSession = null;
    if (typeof usageEventsPlaybackUnsubscribe === 'function') {
        usageEventsPlaybackUnsubscribe();
        usageEventsPlaybackUnsubscribe = null;
    }

    const fallbackTrackId =
        descriptor.trackId ||
        (INITIAL_WORLD_MODE === 'edit' ? DEFAULT_EDIT_TRACK_ID : DEFAULT_PLAY_TRACK_ID);

    const fallback = buildEditModeFallback({ trackId: fallbackTrackId });
    const stacksDefaultsClone = cloneStacksState(fallback.stacksDefaults);
    const stubOrbiter = createFallbackStubOrbiter(fallback.combined, { stacksDefaults: stacksDefaultsClone });

    // Told here too, not only on the path that succeeds. This voice has just stopped being the
    // session it was: `lastResolvedSession` is cleared above and the fallback is what plays now. A
    // figure left holding the previous source would go on drawing the PREVIOUS world's figure in the
    // centre — silently, and for as long as the voice lives, because nothing else would ever correct
    // it. The fallback carries no world release, so this resolves to the mode's bare dark.
    performanceFigure.setSource(fallback.combined);

    const fallbackFlowResult = await initializeBaseFlow({
        worldModeKey: INITIAL_WORLD_MODE,
        worldModeArgs: {
            trackData: fallback.combined,
            graphicsPreference: INITIAL_GRAPHICS_PREFERENCE,
            designDefaults: fallback.designDefaults,
            mappingDefaults: fallback.mappingDefaults,
            stacksDefaults: stacksDefaultsClone,
            stubOrbiter,
        },
        trackData: fallback.combined,
        entangledWorld: fallback.combined?.entangledWorld ?? null,
	        audioEngineOptions: {
	            trackData: fallback.combined ?? null,
	            engineConfig: stubOrbiter?.orbiterData ?? null,
	            userManager: parameterManager,
	            outputNode,
	            transport,
	            // This voice's deck — the adapter seeds it from trackData and reads tempo/sync from it.
	            deck,
	        },
        skipAudioPreload: INITIAL_WORLD_MODE === 'edit'
            ? !trackHasPlayableAudio(fallback.combined?.track)
            : false,
        mappingDefaults: fallback.mappingDefaults,
        stacksDefaults: stacksDefaultsClone,
        stackSelection: fallback.combined?.orbiter?.stackSelection || null,
        designDefaults: fallback.designDefaults,
    });

    if (instanceDisposed) return;

    loadProgress.setStep('uiReady', true);
    if (ownsGlobalLoadingScreen) {
        hideLoadingScreen();
    }

    // Same React-UI shell mount for the edit-mode fallback bootstrap path.
    await ensureReactUIMounted();
    loadProgress.done({ audioReady: fallbackFlowResult?.engineReady !== false });
    settleReady({ ok: true, fallback: true, audioReady: fallbackFlowResult?.engineReady !== false });
}

/**
 * Mount the React UI shell once the engine is ready. React is the only UI, so this
 * always mounts; the dynamic import is just a code-split boundary. Idempotent —
 * `mountOrbitersUI` re-mounts cleanly on re-bootstrap. A mount failure is logged
 * loudly (there is no legacy UI to fall back to).
 */
async function ensureReactUIMounted() {
    // A voice with mountChrome:false renders its 3D scene only (no UI).
    if (!mountChrome || instanceDisposed) return;
    try {
        const { mountOrbitersUI } = await import('./ui/react/mountOrbitersUI.tsx');
        // The instance can be disposed across this import. `uiContainer` is a grid cell that OUTLIVES
        // the voice (the slot layout owns it, so the compositor leaves it in place), and dispose has
        // already run its one unmount — so a root mounted now would never be torn down.
        if (instanceDisposed) return;
        // Single-orbiter mounts to <body> with no voiceId (byte-identical). A multi tile
        // passes its cell + voiceId so the UI fills that tile and reads ITS OWN voice's engine; the
        // primary tile owns the realm-global UI side effects. Keep the handle so dispose unmounts it.
        reactUIHandle = mountOrbitersUI({
            parameterManager,
            container: uiContainer ?? undefined,
            voiceId: uiContainer ? voiceId : undefined,
            isPrimary,
        });
    } catch (error) {
        console.error('[APP] Failed to mount React UI shell:', error);
    }
}

function buildSnapshotFromBridgeDetail(detail = {}) {
    const payload = detail.payload && typeof detail.payload === 'object' ? detail.payload : {};
    const session = detail.session && typeof detail.session === 'object' ? detail.session : {};
    const hydrated = session.hydratedBlobs && typeof session.hydratedBlobs === 'object'
        ? session.hydratedBlobs
        : {};
    const changedRaw = detail.changed || payload.changed || session.changed || [];
    const changed = Array.isArray(changedRaw)
        ? changedRaw
              .map((entry) => (typeof entry === 'string' ? entry.trim() : String(entry ?? '')).trim())
              .filter(Boolean)
        : [];

    const sessionId = sanitizeId(
        payload.sessionId ??
            session.sessionId ??
            session.requested?.sessionId ??
            session.resolved?.sessionId ??
            null,
    );

    return {
        sessionId,
        trackRelease: payload.trackRelease ?? payload.trackSession ?? hydrated.trackSession ?? null,
        orbiterRelease: payload.orbiterRelease ?? payload.orbiterSession ?? hydrated.orbiterSession ?? null,
        entangledWorldRelease:
            payload.entangledWorldRelease ??
            payload.entangledWorldSession ??
            hydrated.entangledWorldSession ??
            null,
        status: payload.status ?? session.status ?? null,
        warnings: payload.warnings ?? session.warnings ?? [],
        changed,
    };
}

function interceptPlaybackSessionLoad(event) {
    if (!playbackLoader) {
        return;
    }
    const detail = event.detail || {};
    const snapshot = buildSnapshotFromBridgeDetail(detail);
    const source = detail.payload?.source || detail.session?.source || 'host-load';
    // A studio swap is an explicit live-descriptor load — never a session-id playback load. Return
    // before the sessionId check so a session id left behind by an earlier playback load can't
    // hijack the swap into `loadBySessionId` (the id is a module-global shared across voices).
    if (source === 'studio-swap') {
        return;
    }
    const hasEmbeddedSnapshot =
        snapshot.trackRelease || snapshot.orbiterRelease || snapshot.entangledWorldRelease;
    const sessionId =
        snapshot.sessionId || lastResolvedSession?.raw?.sessionId || Constants.SESSION_ID || null;
    const rawBase = lastResolvedSession?.raw ?? null;

    // Only intercept for Playback Loader if we have a Session ID (Historical/Playback Mode).
    // If we have blobs but no Session ID, it's likely a Live/Edit load, which sessionController handles.
    if (sessionId) {
        event.stopImmediatePropagation();
        void playbackLoader.loadBySessionId(sessionId, {
            source,
            changed: snapshot.changed,
            rawBase,
        });
        return;
    }

    // Fallback: If it's a pure snapshot (no ID) but explicitly marked as playback source, we might want to intercept.
    // But for 'host-load', we let it fall through to sessionController.
    if (hasEmbeddedSnapshot && source === 'playback-session') {
        event.stopImmediatePropagation();
        void playbackLoader.loadFromSnapshot(snapshot, {
            source,
            sessionId: null,
            changed: snapshot.changed,
            rawBase,
        });
        return;
    }

    // Otherwise, let the event propagate to sessionController

}

function interceptPlaybackSessionUpdate(event) {
    if (!playbackLoader) {
        return;
    }
    const detail = event.detail || {};
    const snapshot = buildSnapshotFromBridgeDetail(detail);
    const source = detail.payload?.source || detail.session?.source || 'host-update';
    const sessionId =
        snapshot.sessionId || lastResolvedSession?.raw?.sessionId || Constants.SESSION_ID || null;
    const rawBase = lastResolvedSession?.raw ?? null;
    const mergeBase = rawBase
        ? {
              ...rawBase,
              track: lastResolvedSession?.track ?? rawBase.track ?? null,
              orbiter: lastResolvedSession?.orbiter ?? rawBase.orbiter ?? null,
              entangledWorld: lastResolvedSession?.entangledWorld ?? rawBase.entangledWorld ?? null,
          }
        : {
              track: lastResolvedSession?.track ?? null,
              orbiter: lastResolvedSession?.orbiter ?? null,
              entangledWorld: lastResolvedSession?.entangledWorld ?? null,
          };

    // Fill missing blobs from the last resolved snapshot so we can apply partial updates
    const mergedSnapshot = {
        ...snapshot,
        trackRelease:
            snapshot.trackRelease ??
            snapshot.trackSession ??
            mergeBase?.trackRelease ??
            mergeBase?.trackSession ??
            null,
        orbiterRelease:
            snapshot.orbiterRelease ??
            snapshot.orbiterSession ??
            mergeBase?.orbiterRelease ??
            mergeBase?.orbiterSession ??
            null,
        entangledWorldRelease:
            snapshot.entangledWorldRelease ??
            snapshot.entangledWorldSession ??
            mergeBase?.entangledWorldRelease ??
            mergeBase?.entangledWorldSession ??
            null,
    };

    const hasEmbeddedSnapshot =
        mergedSnapshot.trackRelease ||
        mergedSnapshot.orbiterRelease ||
        mergedSnapshot.entangledWorldRelease;

    const hasTrack = mergedSnapshot.trackRelease || mergedSnapshot.trackSession || null;

    if (hasTrack) {
        event.stopImmediatePropagation();
        void playbackLoader.loadFromSnapshot(mergedSnapshot, {
            source,
            sessionId,
            changed: snapshot.changed,
            rawBase: mergeBase,
        });
        return;
    }

    if (sessionId) {
        event.stopImmediatePropagation();
        const hydratedBlobs = {
            trackSession: snapshot.trackRelease ?? snapshot.trackSession ?? null,
            orbiterSession: snapshot.orbiterRelease ?? snapshot.orbiterSession ?? null,
            entangledWorldSession: snapshot.entangledWorldRelease ?? snapshot.entangledWorldSession ?? null,
        };
        void playbackLoader.loadBySessionId(sessionId, {
            source,
            changed: snapshot.changed,
            rawBase: mergeBase,
            hydratedBlobs,
        });
    } else {
        console.warn('[Main] session-update ignored: missing sessionId and no embedded snapshot');
    }
}

async function requestInitialSession({ fallbackTrackId }) {
    const bootstrap = buildBootstrapSessionDescriptor({ fallbackTrackId });
    bootstrap.trackId = bootstrap.requested?.trackId ?? null;
    bootstrap.trackVersion = bootstrap.requested?.trackVersion ?? null;
    bootstrap.orbiterId = bootstrap.requested?.orbiterId ?? null;
    bootstrap.orbiterVersion = bootstrap.requested?.orbiterVersion ?? null;
    bootstrap.entangledWorldId = bootstrap.requested?.entangledWorldId ?? null;
    bootstrap.entangledWorldVersion = bootstrap.requested?.entangledWorldVersion ?? null;

    
    const directPayload = getDirectPayloadFromURL(INITIAL_URL_PARAMS);
    const directDescriptor = directPayload
        ? deriveDescriptorFromHydratedPayload(directPayload)
        : null;

    const initialSignatureSeed =
        (directDescriptor && directDescriptor.trackId) ? directDescriptor : (bootstrap.requested || {});

    sessionManager.setInitialSignature(initialSignatureSeed, INITIAL_SESSION_ID ?? null);
    sessionManager.installSessionListeners();
    if (eventBus) {
        eventBus.addEventListener('orbiters:session-ready', handleSessionReady, { passive: true });
        eventBus.addEventListener('orbiters:session-error', handleSessionError);
    }

    if (INITIAL_SESSION_ID) {
        const playbackResult = await playbackLoader.loadBySessionId(INITIAL_SESSION_ID, {
            source: 'playback-session:url',
        });
        if (playbackResult.ok) {
            return;
        }
    }

    if (directPayload) {
        const snapshot = {
            sessionId: null,
            trackRelease: directPayload.trackSession,
            orbiterRelease: directPayload.orbiterSession,
            entangledWorldRelease: directPayload.entangledWorldSession,
        };
        const directResult = await playbackLoader.loadFromSnapshot(snapshot, {
            source: 'hydrated-url',
        });
        if (directResult.ok) {
            return;
        }
    }

    if (INITIAL_SESSION_SOURCE === 'host' && IS_IFRAME) {
        return;
    }

    await sessionManager.processSessionRequest(bootstrap, 'app-init');
}

// The multi-orbiter shell drives these (single-orbiter ignores them — its own
// installed lifecycle handlers call the same closures). suspend/resume mirror the visibilitychange
// branch; dispose tears down this voice's own resources (adapter + visuals).
function suspend() {
    suspendVisualResources();
}
function resume() {
    resumeVisualResources();
    try { startToneContext(); } catch {}
    void audioEngine?.resumeAfterInterruption?.();
}
function dispose() {
    dataManager.dispose();
    disposeOrbiterInstance({ reason: 'shell' });
}

// Expose `worldController` so the multi-orbiter voice factory can register this voice's
// scene+camera with the ViewportCompositor (single-orbiter callers simply ignore it).
// `sceneProfile` is exposed so a REALM can sequence the switch for all its voices at once. The
// renderer is one object shared by every tile, so the realm must not destroy it until each voice has
// released its scene contents through it, and must have rebuilt it before any voice rebuilds into it
// — an ordering no per-voice subscription can guarantee for both directions at once.
return { start, parameterManager, voiceId, worldController, sceneProfile: sceneProfileController, suspend, resume, dispose, whenReady };
}
